import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import DashboardLayout from "@/layouts/DashboardLayout";
import StageTracker from "@/components/base/StageTracker";
import { useNotifications } from "@/context/NotificationContext";
import api from "@/services/api";
import { convertDriveLink } from "@/utils/convertDriveLink";

const STAGES = ["kickoff", "design", "development", "testing", "launch"] as const;

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

interface ProjectUpdate {
  id: string;
  author_name: string;
  author_role: string;
  message: string;
  created_at: string;
}

interface ProjectDetail {
  id: string;
  title: string;
  description: string;
  status: string;
  stage: string;
  progress: number;
  budget: string | null;
  spent: string | null;
  start_date: string | null;
  due_date: string | null;
  tech_stack: string[];
  team: string[];
  screenshots?: string[];
  client_id: string;
  updates?: ProjectUpdate[];
  client?: { company_name: string; full_name: string } | null;
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useNotifications();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [activeTab, setActiveTab] = useState<"updates" | "media" | "team">("updates");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/projects/${id}`)
      .then((res) => setProject(res.data.data))
      .catch(() => showToast("error", "Failed to load project details"))
      .finally(() => setLoading(false));
  }, [id, showToast]);

  const statusColors: Record<string, string> = {
    active: "text-cyber-green border-cyber-green/30 bg-cyber-green/10",
    paused: "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10",
    completed: "text-electric-blue border-electric-blue/30 bg-electric-blue/10",
    cancelled: "text-cyber-red border-cyber-red/30 bg-cyber-red/10",
  };

  const stageIndex = STAGES.indexOf(project?.stage as typeof STAGES[number] ?? "kickoff");

  const handleComment = async () => {
    if (!comment.trim() || !id) return;
    setPosting(true);
    try {
      await api.post(`/projects/${id}/updates`, { message: comment.trim() });
      setComment("");
      // Refetch to show new comment
      const res = await api.get(`/projects/${id}`);
      setProject(res.data.data);
      showToast("success", "Comment posted successfully.");
    } catch {
      showToast("error", "Failed to post comment.");
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <p className="text-white font-bold text-lg mb-2">Project not found</p>
          <Link to="/dashboard/projects" className="text-electric-blue text-sm hover:underline cursor-pointer">
            Back to projects
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const clientName = project.client?.company_name || project.client?.full_name || "Unknown Client";
  const techStack = Array.isArray(project.tech_stack) ? project.tech_stack : [];
  const teamMembers = Array.isArray(project.team) ? project.team : [];
  const updates = project.updates || [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm">
          <Link to="/dashboard/projects" className="text-silver-dim hover:text-electric-blue transition-colors cursor-pointer">
            Projects
          </Link>
          <i className="ri-arrow-right-s-line text-silver-dim" />
          <span className="text-silver truncate">{project.title}</span>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {project.title}
            </h1>
            <p className="text-silver text-sm mt-1">{project.description}</p>
          </div>
          <span
            className={`text-sm px-3 py-1.5 rounded-full border font-medium whitespace-nowrap flex-shrink-0 ${statusColors[project.status]}`}
          >
            {project.status}
          </span>
        </div>

        {/* Stage Tracker */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-6">
          <h2 className="text-white font-bold text-sm mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Project Stage
          </h2>
          <StageTracker currentStage={project.stage} currentIndex={stageIndex} />
          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-full bg-dark-border rounded-full h-2 w-48">
                <div
                  className="bg-electric-blue h-2 rounded-full transition-all"
                  style={{ width: `${project.progress}%` }}
                />
              </div>
              <span className="text-electric-blue text-sm font-bold">{project.progress}%</span>
            </div>
            <div className="text-silver-dim text-xs">
              Due{" "}
              {project.due_date
                ? new Date(project.due_date).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : "TBD"}
            </div>
          </div>
        </div>

        {/* Meta cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Client", value: clientName, icon: "ri-building-line" },
            {
              label: "Start Date",
              value: project.start_date
                ? new Date(project.start_date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : "TBD",
              icon: "ri-calendar-line",
            },
            {
              label: "Due Date",
              value: project.due_date
                ? new Date(project.due_date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : "TBD",
              icon: "ri-time-line",
            },
            { label: "Team Size", value: `${teamMembers.length} members`, icon: "ri-team-line" },
          ].map((m) => (
            <div key={m.label} className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${m.icon} text-electric-blue text-sm`} />
                </div>
                <span className="text-silver-dim text-xs uppercase tracking-wider">{m.label}</span>
              </div>
              <span className="text-white text-sm font-semibold">{m.value}</span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
          <div className="flex border-b border-dark-border">
            {(["updates", "media", "team"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-3.5 text-sm font-medium capitalize transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab
                    ? "text-electric-blue border-b-2 border-electric-blue"
                    : "text-silver hover:text-white"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-5">
            {activeTab === "updates" && (
              <div className="space-y-5">
                {updates.map((upd) => (
                  <div key={upd.id} className="flex gap-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-electric-blue/20 border border-electric-blue/30 text-electric-blue text-xs font-bold flex-shrink-0">
                      {upd.author_name.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white text-sm font-semibold">{upd.author_name}</span>
                        <span className="text-silver-dim text-xs">{upd.author_role}</span>
                        <span className="text-silver-dim text-xs ml-auto">{timeAgo(upd.created_at)}</span>
                      </div>
                      <p className="text-silver text-sm leading-relaxed">{upd.message}</p>
                    </div>
                  </div>
                ))}

                {updates.length === 0 && (
                  <p className="text-silver-dim text-sm text-center py-4">No updates yet.</p>
                )}

                {/* Comment box */}
                <div className="border-t border-dark-border pt-5">
                  <label className="block text-silver text-xs font-medium mb-2 uppercase tracking-wider">
                    Add a Comment
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                    placeholder="Ask a question or leave a note..."
                    className="w-full bg-deep-black border border-dark-border rounded-xl px-4 py-3 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors resize-none"
                  />
                  <button
                    onClick={handleComment}
                    disabled={posting || !comment.trim()}
                    className="mt-3 bg-electric-blue text-deep-black font-semibold px-5 py-2 rounded-lg text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {posting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-3.5 h-3.5 border-2 border-deep-black/30 border-t-deep-black rounded-full animate-spin" />
                        Posting...
                      </span>
                    ) : (
                      "Post Comment"
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeTab === "media" && (
              <div>
                {Array.isArray(project.screenshots) && project.screenshots.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {project.screenshots.map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="block">
                        <img
                          src={convertDriveLink(url)}
                          alt={`Screenshot ${idx + 1}`}
                          className="w-full aspect-video object-cover rounded-xl border border-dark-border hover:border-electric-blue/50 transition-colors"
                          referrerPolicy="no-referrer"
                        />
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-dark-panel border border-dark-border mx-auto mb-4">
                      <i className="ri-image-line text-2xl text-silver-dim" />
                    </div>
                    <p className="text-white font-semibold mb-1">No media uploaded yet</p>
                    <p className="text-silver-dim text-sm">Project files and assets will appear here.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "team" && (
              <div className="space-y-3">
                {teamMembers.map((member) => (
                  <div
                    key={member}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-dark-panel transition-colors"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-electric-blue/20 border border-electric-blue/30 text-electric-blue text-sm font-bold flex-shrink-0">
                      {member.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{member}</p>
                      <p className="text-silver-dim text-xs">Team Member</p>
                    </div>
                  </div>
                ))}
                {teamMembers.length === 0 && (
                  <p className="text-silver-dim text-sm text-center py-4">No team members assigned.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tech Stack */}
        <div className="bg-dark-card border border-dark-border rounded-2xl p-5">
          <h2 className="text-white font-bold text-sm mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Technology Stack
          </h2>
          <div className="flex flex-wrap gap-2">
            {techStack.map((t) => (
              <span
                key={t}
                className="text-sm px-3 py-1.5 rounded-lg bg-deep-black border border-dark-border text-silver hover:border-electric-blue/40 hover:text-electric-blue transition-colors"
              >
                {t}
              </span>
            ))}
            {techStack.length === 0 && (
              <p className="text-silver-dim text-sm">No technologies listed.</p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

